import { message } from 'antd';
import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import baseConfig from '@/config';
import {
  deleteVideo,
  getTaskList,
  getVideoList,
  type TaskListRsType,
  type VideoRsDataType,
} from '@/services/avatars';
import { fetchPersonalList } from '@/services/lipSync';
import {
  fetchGroupCodesData,
  fetchTemplatesData,
  getPackageCodes,
} from '@/services/template';
import {
  deleteDraft,
  getDraftList as getDraft,
  type IDeleteDraft,
} from '@/services/workspace';
import {
  GroupCodes,
  ModuleKeyType,
  ModuleValueType,
  PACKAGE_MODULE,
} from '@/utils/const';

export type ListType = {
  projectId: number;
  projectName: string;
  status: number;
  fileSize: number;
  downloadUrl: string;
  coverUrl: string;
  desc: string;
  createTime: number;
  updateTime: number;
  extendInfo: string;
  [key: string]: any;
};

export type AvatarsStoreProps = {
  /**
   * 任务列表
   */
  // exportList: TaskListRsType[];
  // getExportList: () => void;
  // isExportListEmpty: boolean;
  taskTotal: number;

  /**
   * 导出的视频列表
   */
  // 创建已完成+模板的
  avatarsList: VideoRsDataType[];
  //创建已完成
  customSuccessAvatarsList: [];
  // 已完成+创建中
  customExceptFailedAvatarsList: [];
  // 模板的
  templateAvatarsList: [];
  // 自定义的全部（包括异常）
  customAvatarsList: (VideoRsDataType & { cover: string; state: number })[];
  // 自定义各种异常的
  taskAvatarsList: (VideoRsDataType & { cover: string; state: number })[];
  isAvatarsListEmpty: boolean;
  isTaskAvatarsListEmpty: boolean;
  getAvatarsList: (cb?: (data: any) => void) => void;
  getCustomAvatarsList: () => any;
  getTemplateAvatarsList: () => any;
  delItem: (id: number) => void;
  avatarsTotal: number;
  avatarsPage: number;

  /**
   * 草稿列表
   */
  draftList: ListType[];
  getDraftList: () => void;
  draftTotal: number;
  isDraftListEmpty: boolean;
  deleteDraft: (params: IDeleteDraft) => void;

  /**
   *类型- 展示全部时需要区分
   */
  viewAllType: 'my' | 'hot' | '';
  setViewAllType: (type: 'my' | 'hot' | '') => void;

  resetAvatarsStore: () => void;

  /**
   * 剩余座位
   */
  seats: {
    available: string | number;
    total: string | number;
  };
  setSeats: (seats: { available: string | number; total: string | number }) => void;

  // 自定义数字人全部原始数据
  customAvatarOriginData: any[];
  // 模板数字人全部原始数据
  templateAvatarOrginData: any[];
  // 视频是否静音
  videoMute: boolean;
  setVideoMute: (videoMute: boolean) => void;
};

const useAvatarsStore = createWithEqualityFn<AvatarsStoreProps>()(
  devtools(
    (set, get) => ({
      avatarsList: [],
      customExceptFailedAvatarsList: [],
      customSuccessAvatarsList: [],
      taskAvatarsList: [],
      templateAvatarsList: [],
      customAvatarsList: [],
      isAvatarsListEmpty: true,
      isTaskAvatarsListEmpty: true,
      avatarsTotal: 0,
      avatarsPage: 1,
      taskTotal: 0,

      customAvatarOriginData: [],
      templateAvatarOrginData: [],

      // 获取素材库的数字人列表
      getTemplateAvatarsList: () => {
        return new Promise(async (resolve, reject) => {
          const data = await getPackageCodes('Avatar');

          if (typeof data === 'string') {
            if (data) {
              const templateData = await fetchTemplatesData({
                groupCodes: data,
                pageNum: 1,
                pageSize: 1000,
              });
              if (templateData.isError) {
                reject(templateData);

                set({ templateAvatarOrginData: [] });
                return;
              }

              set({ templateAvatarOrginData: templateData.data });
              resolve(templateData);
            } else {
              set({ templateAvatarOrginData: [] });
              resolve({ data: [] });
            }
          } else {
            if (data.isError) {
              set({ templateAvatarOrginData: [] });
              reject(data);
              return;
            }
          }
        });
      },
      // 获取自定义的数字人列表
      getCustomAvatarsList: () => {
        // type固定传1
        return new Promise(async (resolve, reject) => {
          const data = await fetchPersonalList({
            type: 1,
            pageNum: 1,
            pageSize: 1000,
          });
          if (data.isError) {
            set({ customAvatarOriginData: [] });
            reject(data);
            return;
          }
          if (data.items) {
            set({ customAvatarOriginData: data.items });
          }
          resolve(data);
        });
      },
      getAvatarsList: async (
        cb?: (data: { customAvatarsList: any; templateAvatarsList: any }) => void,
      ) => {
        try {
          const { getCustomAvatarsList, getTemplateAvatarsList } = get();
          const [customAvatarsResult, templateAvatarsResult] = await Promise.allSettled([
            getCustomAvatarsList(),
            getTemplateAvatarsList(),
          ]);
          let totalArray: any = [];
          let customSuccessArray: any = [];
          let taskArray: any = [];
          let customArray: any = [];
          let customExceptFailedAvatarsList: any = [];
          let total: number = 0;

          if (customAvatarsResult.status === 'fulfilled') {
            if (customAvatarsResult.value.success) {
              const customAvatars = customAvatarsResult.value;
              let formatArray = customAvatars.items.map((custom: any) => {
                return {
                  ...custom,
                  cover: custom.previewUrl || '',
                  fileUrl: custom.exampleVideoUrl || custom.originUrl,
                  isVcm: false,
                };
              });
              customArray.push(...(formatArray ?? []));

              // 2是已完成
              customSuccessArray = formatArray.filter((format: any) => {
                if (format.state === 1 || format.state === 2) {
                  customExceptFailedAvatarsList.push(format);
                }
                return format.state === 2;
              });
              taskArray = formatArray.filter((format: any) => format.state !== 2);
              totalArray.push(...(customSuccessArray ?? []));

              total += customAvatars.totalCount;
            }
          } else {
            message.error(customAvatarsResult.reason?.message || '获取自定义数字人失败');
          }

          let templateAvatarsList: any = [];
          if (templateAvatarsResult.status === 'fulfilled') {
            if (templateAvatarsResult.value.success) {
              const templateAvatars = templateAvatarsResult.value;
              let formatArray = templateAvatars.data.map((template: any) => {
                return {
                  ...template,
                  id: template.templateCode,
                  cover: template.icon,
                  fileUrl:
                    JSON.parse(template.immutableExtend).tobPreviewVideo ||
                    template.previewUrl,
                  isVcm: true,
                  gender: JSON.parse(template.immutableExtend).gender,
                  associatedVoiceId: JSON.parse(template.immutableExtend).timbreId,
                };
              });
              templateAvatarsList.push(...(formatArray ?? []));
              totalArray.push(...(formatArray ?? []));
            }
          } else {
            message.error(templateAvatarsResult.reason?.message || '获取素材数字人失败');
          }

          set({
            taskTotal: taskArray.length,
            avatarsList: totalArray, // 创建已完成+模板的
            customSuccessAvatarsList: customSuccessArray, //创建已完成
            taskAvatarsList: taskArray, // 自定义各种异常的
            customAvatarsList: customArray, // 自定义的全部（包括异常）
            customExceptFailedAvatarsList,
            templateAvatarsList: templateAvatarsList, // 模板的
            isAvatarsListEmpty: customArray.length === 0,
            isTaskAvatarsListEmpty: taskArray.length === 0,
            avatarsTotal: total,
          });

          cb &&
            cb({
              customAvatarsList: customArray,
              templateAvatarsList,
            });
        } catch (error) {}
      },

      delItem: async (id: number) => {
        const { getAvatarsList } = get();
        const res = await deleteVideo({ ids: [id] });
        if (res.success) {
          getAvatarsList();
          message.success('删除成功');
        } else {
          message.warning(res.message);
        }
      },

      draftList: [],
      draftTotal: 0,
      getDraftList: async () => {
        const res = await getDraft<{
          success: boolean;
          message: string;
          items: ListType[];
          totalCount: number;
        }>({
          pageNum: 1,
          pageSize: 1000,
        });
        if (res.success) {
          let arr = res.items.map((item) => {
            // @ts-ignore
            item.duration = JSON.parse(item.extendInfo || '{}').duration || 0;
            return item;
          });
          set({
            draftList: arr,
            isDraftListEmpty: res.items.length === 0,
            draftTotal: res.totalCount,
          });
        } else {
          message.warning(res.message);
        }
      },
      isDraftListEmpty: true,
      deleteDraft: async (params: IDeleteDraft) => {
        const { getDraftList } = get();
        const res = await deleteDraft(params);
        if (res.code !== 200) return;
        getDraftList();
        message.success('删除成功');
      },
      viewAllType: '',
      setViewAllType: (viewAllType: 'my' | 'hot' | '') => set({ viewAllType }),
      resetAvatarsStore: () => {
        set({
          viewAllType: '',
          isDraftListEmpty: true,
          isAvatarsListEmpty: true,
          isTaskAvatarsListEmpty: true,
          // isExportListEmpty: true,
          avatarsList: [],
          customSuccessAvatarsList: [],
          taskAvatarsList: [],
          templateAvatarsList: [],
          customAvatarsList: [],
          // exportList: [],
          draftList: [],
          taskTotal: 0,
          draftTotal: 0,
          avatarsTotal: 0,

          avatarsPage: 1,
        });
      },
      seats: {
        available: '-',
        total: '-',
      },
      setSeats: (seats: { available: string | number; total: string | number }) => {
        set({ seats });
      },

      videoMute: true,
      setVideoMute: (videoMute: boolean) => {
        set({ videoMute });
      },
    }),
    {
      name: 'avatars_store',
      enabled: baseConfig.debuggable,
    },
  ),
  shallow,
);

export default useAvatarsStore;
